/* eslint-disable @typescript-eslint/no-explicit-any */
import { IdentityManager } from '../utils/identity';
import { isTrackingSuppressed } from "../utils/suppression";
import { getCookie } from '../utils/storage';

// ============================================================================
// CONSENT
// ============================================================================

export type ConsentCategory = 'analytics' | 'marketing';
export type BotDetectionMode = 'block' | 'tag' | 'disabled';

export interface FlowGridTransportConfig {
  /** Bot detection mode: 'block' skips events, 'tag' adds _bot flag, 'disabled' sends all */
  botDetection?: BotDetectionMode;
  /** Respect navigator.doNotTrack / globalPrivacyControl (default true) */
  respectDNT?: boolean;
  /** Global sampling rate 0.0–1.0 (default 1.0 = send everything) */
  sampleRate?: number;
  /**
   * Send events from localhost / 127.0.0.1 / *.localhost (default false).
   * Local development traffic pollutes production analytics, so tracking is
   * suppressed on local hosts unless this is explicitly enabled — e.g. when
   * verifying an integration end-to-end before going live.
   */
  trackLocalhost?: boolean;
}

/**
 * SDK Feature Types
 */
export type FeatureType = 
  | "engagement"
  | "cohorts"
  | "churn"
  | "monetization"
  | "multi_path_funnels"
  | "support"
  | "acquisition"
  | "paths"
  | "alerts"
  | "security"
  | "forecasting";

/**
 * FlowGridTransport — Unified transport layer for Flowgrid SDK.
 *
 * @description
 * Provides a secure, validated RPC-style transport layer used by all SDK modules.
 * Handles:
 * - Input validation
 * - Payload size enforcement
 * - Timeout handling
 * - Retry logic (network failures only)
 * - Authorization headers
 * - Feature-scoped endpoint routing
 *
 * This class is designed to be extended by higher-level modules
 * (e.g., Analytics, Experiments, Engagement, Cohorts).
 *
 * @example
 * ```ts
 * const client = new FlowGridTransport("web_123", "https://core.flow-grid.xyz", "sk_live_xxx");
 * ```
 */
export class FlowGridTransport {

  /**
   * Unique Web ID identifying the site or application.
   * Used by the backend to associate incoming events.
   */
  protected webId: string;

  /**
   * Base API endpoint.
   * Example values:
   * - "core.flow-grid.xyz"
   * - "https://core.flow-grid.xyz"
   *
   * Protocol is normalized automatically.
   */
  protected endpoint: string;

  /**
   * Private API key used for authenticating requests.
   * Sent as a Bearer token in the Authorization header.
   */
  protected apiKey: string;

  /**
   * Visitor ID for cross-session visitor tracking.
   * Persisted per-visitor (cookie/localStorage) and sent with every event.
   */
  protected visitorId: string;

  /**
   * Optional identity manager that owns the visitor/session lifecycle.
   * When provided, visitor_id is resolved at call time (not construction time).
   */
  protected identityManager?: IdentityManager;

  /**
   * Maximum allowed payload size (in bytes).
   * Prevents oversized event submissions.
   */
  private static readonly MAX_PAYLOAD_SIZE = 10_000; // 10KB

  /**
   * Maximum time (ms) before request is aborted.
   */
  private static readonly TIMEOUT_MS = 10_000;

  /**
   * Maximum retry attempts for network-level failures and 5xx server errors.
   */
  private static readonly MAX_RETRIES = 3;

  /** Max events to persist in localStorage buffer */
  private static readonly MAX_BUFFER_EVENTS = 100;

  /** Max buffer size in bytes */
  private static readonly MAX_BUFFER_SIZE = 50_000; // 50KB

  /** localStorage key for failed event buffer */
  private static readonly EVENT_BUFFER_KEY = 'fg_event_buffer';

  /** Consent state */
  private static consent: Record<ConsentCategory, boolean> = { analytics: true, marketing: true };

  /** Transport config */
  protected transportConfig: FlowGridTransportConfig;

  /**
   * Creates a new FlowGridTransport instance.
   *
   * @param webId - Unique identifier for the web property.
   * @param endpoint - API base endpoint.
   * @param apiKey - Private API key for authentication.
   * @param visitorId - Optional visitor ID for cross-session tracking.
   *
   * @throws {Error} If required parameters are missing.
   */
  constructor(webId: string, endpoint: string, apiKey: string, visitorId?: string, identityManager?: IdentityManager, config?: FlowGridTransportConfig) {
    if (!webId?.trim() || !endpoint?.trim()) {
      throw new Error("SDK init failed: webId and endpoint are required.");
    }

    this.webId = webId.trim();
    this.endpoint = this.normalizeEndpoint(endpoint);
    this.apiKey = apiKey;
    this.identityManager = identityManager;
    this.visitorId = visitorId ?? '';
    this.transportConfig = {
      botDetection: config?.botDetection ?? 'block',
      respectDNT: config?.respectDNT ?? true,
      sampleRate: config?.sampleRate ?? 1.0,
      trackLocalhost: config?.trackLocalhost ?? false,
    };

    // Drain buffered events from previous failed attempts
    this.drainEventBuffer();
  }

  // ---------------------------------------------------------------------------
  // CONSENT API
  // ---------------------------------------------------------------------------

  static setConsent(consent: Partial<Record<ConsentCategory, boolean>>): void {
    Object.assign(FlowGridTransport.consent, consent);
  }

  static hasConsent(category: ConsentCategory): boolean {
    return FlowGridTransport.consent[category] ?? false;
  }

  /** Pages already debug-noted as tracking-blocked (one note per page, not per event). */
  private static pathBlockedNoted = new Set<string>();

  /**
   * One quiet, non-throwing note per failure signature. Analytics must never
   * break or pollute the host app: a failed tracking request is FlowGrid's
   * problem, not the customer's — surfacing it as an exception puts it in
   * framework dev overlays, window.onerror handlers and the customer's own
   * error tracking (Sentry & co.), none of which should ever see FlowGrid
   * internals. `console.debug` keeps it discoverable at verbose log level.
   */
  private static requestFailureNoted = new Set<string>();
  private static noteRequestFailure(detail: string): void {
    if (FlowGridTransport.requestFailureNoted.has(detail)) return;
    FlowGridTransport.requestFailureNoted.add(detail);
    console.debug(`[FlowGrid] Request not delivered (${detail}) — the event was buffered or dropped. FlowGrid never throws; this note is debug-only.`);
  }

  /**
   * One-time, non-error note that this page's events are dropped by the site's
   * page-blocking rules. `console.debug` on purpose: visible when a developer
   * goes looking (verbose log level), invisible otherwise — never an exception.
   */
  private static notePathBlocked(): void {
    const page = typeof location !== 'undefined' ? location.pathname : '(unknown page)';
    if (FlowGridTransport.pathBlockedNoted.has(page)) return;
    FlowGridTransport.pathBlockedNoted.add(page);
    console.debug(
      `[FlowGrid] Tracking on ${page} is disabled by this site's page rules — events from this page are dropped.`
    );
  }

  // ---------------------------------------------------------------------------
  // LOCALHOST SUPPRESSION
  // ---------------------------------------------------------------------------

  private static localhostNoted = false;

  /** True when the page is served from a local development host. */
  private static isLocalHostname(): boolean {
    if (typeof location === 'undefined') return false;
    const host = (location.hostname || '').toLowerCase();
    return (
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host === '::1' ||
      host === '[::1]' ||
      host === '0.0.0.0' ||
      host.endsWith('.localhost') ||
      location.protocol === 'file:'
    );
  }

  /**
   * Central localhost gate consulted by every send path. When suppressing,
   * logs a single note so developers know why no events appear — and how to
   * turn tracking back on for integration testing.
   */
  private suppressLocalTracking(): boolean {
    if (this.transportConfig.trackLocalhost === true) return false;
    if (!FlowGridTransport.isLocalHostname()) return false;
    if (!FlowGridTransport.localhostNoted) {
      FlowGridTransport.localhostNoted = true;
      console.info(
        '[FlowGrid] Tracking is disabled on localhost. Pass { trackLocalhost: true } in the transport config to enable it for integration testing.'
      );
    }
    return true;
  }

  // ---------------------------------------------------------------------------
  // BOT DETECTION
  // ---------------------------------------------------------------------------

  private static isLikelyBot(): boolean {
    if (typeof navigator === 'undefined') return false;
    if ((navigator as any).webdriver) return true;
    if (/bot|crawl|spider|headless|phantom|puppeteer/i.test(navigator.userAgent)) return true;
    return false;
  }

  // ---------------------------------------------------------------------------
  // DNT / GPC CHECK
  // ---------------------------------------------------------------------------

  private isDNTEnabled(): boolean {
    if (typeof navigator === 'undefined') return false;
    if ((navigator as any).globalPrivacyControl) return true;
    if (navigator.doNotTrack === '1') return true;
    return false;
  }

  // ---------------------------------------------------------------------------
  // DEVICE ENRICHMENT
  // ---------------------------------------------------------------------------

  private static getDeviceContext(): Record<string, unknown> {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return {};
    const loc = window.location;
    const doc = typeof document !== 'undefined' ? document : undefined;
    return {
      _screen_width: window.screen?.width,
      _screen_height: window.screen?.height,
      _device_type: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
      _language: navigator.language,
      // Page context — auto-attached so every event (custom or named) records
      // which page it fired on, enabling per-page breakdowns downstream.
      _url: loc?.href,
      _page: loc?.pathname,
      _referrer: doc?.referrer || undefined,
      _title: doc?.title || undefined,
      // Marketing attribution — auto-captured so EVERY event (feature_usage
      // included) carries the campaign that drove it, with zero customer config.
      // Lets the dashboard segment usage/adoption by source/campaign
      // (Mixpanel-parity). Only present keys are attached.
      ...FlowGridTransport.getUtmContext(),
    };
  }

  /**
   * The attribution params FlowGrid tracks — kept in lockstep with
   * flowgrid-core's `public/flowgrid.js` (`captureFromQueryOrCookie` and
   * `AD_CLICK_IDS`) and the `getUTM()` reader in `utils/storage`. Five UTMs,
   * `ref`/`via`/`source`, and every ad-platform click id.
   *
   * The cookie fallback matters most for the click ids: they appear only on the
   * landing URL, so without `__flowgrid_<param>` every event after the first
   * pageview would lose the paid attribution that brought the visitor in.
   */
  private static readonly ATTRIBUTION_KEYS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'ref', 'via', 'source',
    // Ad-platform click ids. These matter as much as the UTMs: platforms
    // auto-tag with a click id and NO utm_* at all (Google Ads' default), so
    // without these a paid visit carries zero attribution and every report
    // files it under "direct". A click id is also an unambiguous paid signal —
    // it only exists because someone clicked a paid placement.
    // Keep in lockstep with `AD_CLICK_IDS` in flowgrid-core public/flowgrid.js.
    'gclid', 'gbraid', 'wbraid', 'dclid', 'msclkid',
    'fbclid', 'ttclid', 'li_fat_id', 'twclid', 'rdt_cid',
    // Google auxiliary params — not click ids (they identify nobody), but the
    // only campaign signal present when auto-tagging replaces utm_campaign.
    'gad_source', 'gad_campaignid',
  ] as const;

  /**
   * Resolves marketing attribution into `_utm_source`/`_ref`/… properties.
   *
   * Prefers a fresh value from the current URL (the live campaign), falling back
   * to the `__flowgrid_<name>` cookie that `flowgrid.js` persists on the landing
   * page — so events fired on *later* pages (where the URL no longer carries
   * UTMs) still attribute to the campaign that brought the visitor in. Same
   * cookie names + key set as the loader script. Returns `{}` for non-campaign
   * traffic so the payload stays clean.
   */
  private static getUtmContext(): Record<string, string> {
    if (typeof window === 'undefined') return {};
    // Marketing attribution is `marketing`-category data (see ConsentCategory):
    // when the visitor declined marketing consent, events still flow (analytics
    // permitting) but carry no campaign/UTM/ref attribution.
    if (!FlowGridTransport.hasConsent('marketing')) return {};
    let params: URLSearchParams | null = null;
    try {
      params = new URLSearchParams(window.location?.search ?? '');
    } catch {
      params = null;
    }
    const out: Record<string, string> = {};
    for (const name of FlowGridTransport.ATTRIBUTION_KEYS) {
      const value = params?.get(name) || getCookie(`__flowgrid_${name}`);
      if (value) out[`_${name}`] = value;
    }
    return out;
  }

  // ---------------------------------------------------------------------------
  // SENDBACON FALLBACK
  // ---------------------------------------------------------------------------

  /**
   * `_session_id` on every browser event, like the `_page`/`_utm_*` context.
   *
   * Without it no SDK event said which visit it belonged to, so the session id
   * a checkout carried (`checkoutAttribution()`) had nothing to match in
   * Flowgrid. Browser-only: a server process has no visit, and the identity
   * manager's in-memory session there would stamp every request with one
   * meaningless id. Underscored so a customer's own `session_id` is untouched.
   */
  protected sessionContext(): { _session_id?: string } {
    if (!this.identityManager || typeof window === 'undefined') return {};
    try {
      const id = this.identityManager.getSessionId();
      return id ? { _session_id: id } : {};
    } catch {
      return {};
    }
  }

  /**
   * Fire-and-forget event via sendBeacon. Used for page unload events.
   */
  sendViaBeacon(eventName: string, properties: Record<string, any> = {}): boolean {
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return false;
    if (this.suppressLocalTracking()) return false;

    const resolvedVisitorId = this.identityManager
      ? this.identityManager.getVisitorId()
      : (this.visitorId ?? '');

    const callType = properties.type || 'event';
    const url = `${this.endpoint}/api/v1/sdk/functions?call=${encodeURIComponent(callType)}`;
    const payload = JSON.stringify({
      web_id: this.webId,
      visitor_id: resolvedVisitorId,
      event_name: eventName,
      properties: { ...properties, ...FlowGridTransport.getDeviceContext(), ...this.sessionContext() },
    });

    return navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
  }

  // ---------------------------------------------------------------------------
  // RAW LARGE-PAYLOAD POST (session replay chunks, etc.)
  // ---------------------------------------------------------------------------

  /**
   * POST a large opaque payload to a dedicated SDK endpoint, bypassing the
   * 10KB validator. Used by session replay (`call=replay_chunk`) where each
   * batch of rrweb events can be hundreds of kilobytes.
   *
   * Falls back to `navigator.sendBeacon` on `pagehide` / final flush.
   */
  protected async postRaw(
    callType: string,
    body: Record<string, unknown>,
    options: { useBeacon?: boolean } = {},
  ): Promise<void> {
    if (FlowGridTransport.consent.analytics === false) return;
    if (this.transportConfig.respectDNT !== false && this.isDNTEnabled()) return;
    if (this.suppressLocalTracking()) return;

    const url = `${this.endpoint}/api/v1/sdk/functions?call=${encodeURIComponent(callType)}`;
    const resolvedVisitorId = this.identityManager
      ? this.identityManager.getVisitorId()
      : (this.visitorId ?? '');

    const payload = JSON.stringify({
      web_id: this.webId,
      visitor_id: resolvedVisitorId,
      ...body,
    });

    // Transport strategy:
    //   - Regular flushes use plain fetch (no keepalive). `fetch + keepalive`
    //     has a hard 64 KB request-body limit per the spec — anything larger
    //     (notably the rrweb FullSnapshot chunk, which is typically 100KB-500KB+)
    //     is silently rejected by the browser, dropping sequence 0 and leaving
    //     the player with no DOM to render.
    //   - Unload flushes (visibilitychange/pagehide/stop with `useBeacon: true`)
    //     MUST use keepalive so the request survives navigation. If the payload
    //     is also too large for keepalive, fall back to navigator.sendBeacon —
    //     it can't set Authorization headers, so the server's body-auth fallback
    //     (api_key in the JSON body) picks it up via the proxy middleware.
    const KEEPALIVE_BYTE_LIMIT = 60_000; // a hair under the 64 KiB spec cap
    const isUnload = options.useBeacon === true;
    const payloadSize = payload.length;
    const useKeepalive = isUnload && payloadSize <= KEEPALIVE_BYTE_LIMIT;

    if (isUnload && payloadSize > KEEPALIVE_BYTE_LIMIT) {
      // Too large for keepalive; sendBeacon survives unload but strips headers.
      // Inject api_key into the body so the proxy middleware's body-auth fallback
      // can authenticate the request without an Authorization header.
      const beaconBody = JSON.stringify({ ...JSON.parse(payload), api_key: this.apiKey });
      try {
        const ok = typeof navigator !== 'undefined'
          && typeof navigator.sendBeacon === 'function'
          && navigator.sendBeacon(url, new Blob([beaconBody], { type: 'application/json' }));
        if (!ok && typeof console !== 'undefined') {
          console.warn(`[Flowgrid] ${callType} beacon rejected (payload ${payloadSize} bytes)`);
        }
      } catch (err) {
        if (typeof console !== 'undefined') {
          console.warn(`[Flowgrid] ${callType} beacon error:`, err);
        }
      }
      return;
    }

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
        },
        body: payload,
        ...(useKeepalive ? { keepalive: true } : {}),
      });
      // Log server rejections so developers can diagnose silently-dropped chunks
      // (e.g. invalid API key → 403, missing fields → 400, chunk too large → 413).
      if (!res.ok && typeof console !== 'undefined') {
        console.warn(`[Flowgrid] ${callType} rejected: ${res.status} ${res.statusText} (${payloadSize} bytes)`);
      }
    } catch (err) {
      // Best-effort, but surface the failure so silently-dropped chunks are
      // visible during development.
      if (typeof console !== 'undefined') {
        console.warn(`[Flowgrid] ${callType} network error (${payloadSize} bytes):`, err);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // EVENT BUFFER (localStorage persistence)
  // ---------------------------------------------------------------------------

  private bufferEvent(url: string, method: string, body?: string): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(FlowGridTransport.EVENT_BUFFER_KEY);
      const buffer: Array<{ url: string; method: string; body?: string }> = raw ? JSON.parse(raw) : [];

      if (buffer.length >= FlowGridTransport.MAX_BUFFER_EVENTS) return;

      const serialized = JSON.stringify([...buffer, { url, method, body }]);
      if (new TextEncoder().encode(serialized).length > FlowGridTransport.MAX_BUFFER_SIZE) return;

      buffer.push({ url, method, body });
      localStorage.setItem(FlowGridTransport.EVENT_BUFFER_KEY, JSON.stringify(buffer));
    } catch { /* storage unavailable */ }
  }

  private drainEventBuffer(): void {
    if (typeof localStorage === 'undefined') return;
    // Don't replay buffered events from a local dev session either.
    if (this.suppressLocalTracking()) return;
    try {
      const raw = localStorage.getItem(FlowGridTransport.EVENT_BUFFER_KEY);
      if (!raw) return;
      const buffer: Array<{ url: string; method: string; body?: string }> = JSON.parse(raw);
      localStorage.removeItem(FlowGridTransport.EVENT_BUFFER_KEY);

      for (const evt of buffer) {
        // Best-effort replay; don't await
        fetch(evt.url, {
          method: evt.method,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.apiKey}`,
          },
          body: evt.body,
        }).catch(() => {});
      }
    } catch { /* storage unavailable */ }
  }

  /**
   * Core RPC transport method for standard SDK calls.
   *
   * @template T Expected JSON response type.
   *
   * @param eventName - Logical event or function name.
   * @param properties - Arbitrary event payload properties.
   * @param method - HTTP method ("GET", "POST", "PATCH").
   *
   * @returns Promise resolving to typed response.
   */
  protected async init<T>(
    eventName: string,
    properties: Record<string, any> = {},
    method: "GET" | "POST" | "PATCH" = "POST"
  ): Promise<T> {

    // Global kill switch — outranks consent, sampling and every per-feature
    // setting. Checked FIRST and here, at the single choke point every send
    // passes through, so no feature can forget it. Cookie-backed so the server
    // side honours the same opt-out (utils/suppression.ts).
    if (isTrackingSuppressed()) {
      return {} as T;
    }

    // Consent gate
    if (!FlowGridTransport.hasConsent('analytics')) {
      return {} as T;
    }

    // DNT / GPC gate
    if (this.transportConfig.respectDNT && this.isDNTEnabled()) {
      return {} as T;
    }

    // Bot detection
    if (this.transportConfig.botDetection === 'block' && FlowGridTransport.isLikelyBot()) {
      return {} as T;
    }

    this.validateInput(eventName, properties);

    // Bot tagging
    if (this.transportConfig.botDetection === 'tag' && FlowGridTransport.isLikelyBot()) {
      properties = { ...properties, _bot: true };
    }

    // Auto device enrichment
    const enriched: Record<string, any> = { ...FlowGridTransport.getDeviceContext(), ...this.sessionContext(), ...properties };

    const callType = enriched.type || "event";
    const url = `${this.endpoint}/api/v1/sdk/functions?call=${encodeURIComponent(callType)}`;

    const resolvedVisitorId = this.identityManager
      ? this.identityManager.getVisitorId()
      : (this.visitorId ?? '');

    const body = JSON.stringify({
      web_id: this.webId,
      visitor_id: resolvedVisitorId,
      event_name: eventName,
      properties: enriched,
    });

    return this.executeRequest<T>(url, method, method === "GET" ? undefined : body);
  }

  /**
   * Feature-scoped RPC transport method.
   * Routes to the SDK API endpoint with a feature type qualifier.
   *
   * @param eventName - Name of the event to track.
   * @param properties - Additional properties to send (must include type).
   */
  protected async initFeature<T>(eventName: string, properties: Record<string, any> & { type: FeatureType }): Promise<T> {

    // Second send path — it does NOT go through init(), so the global kill
    // switch has to be repeated here. Every `return {} as T` bail in this file
    // needs the same guard above it for the override to be truly global.
    if (isTrackingSuppressed()) {
      return {} as T;
    }

    this.validateInput(eventName, properties);

    if (!properties.type) {
      throw new Error('Feature events must include a type property');
    }

    const url = `${this.endpoint}/api/v1/sdk/functions?call=${encodeURIComponent(properties.type)}`;

    const resolvedVisitorId = this.identityManager
      ? this.identityManager.getVisitorId()
      : (this.visitorId ?? '');

    const body = JSON.stringify({
      web_id: this.webId,
      visitor_id: resolvedVisitorId,
      event_name: eventName,
      properties,
    });

    return this.executeRequest(url, "POST", body);
  }

  /**
   * Executes HTTP request with timeout and retry handling.
   *
   * @template T Expected response type.
   *
   * @param url - Fully constructed request URL.
   * @param method - HTTP method.
   * @param body - Optional JSON body (POST/PATCH only).
   * @param extraHeaders - Additional headers to include.
   *
   * Never throws for transport problems (timeout, network failure, non-OK
   * status): failures are debug-noted, buffered/beaconed where possible, and
   * resolve to an empty object so tracking can never break the host app.
   */
  private async executeRequest<T>(
    url: string,
    method: "GET" | "POST" | "PATCH",
    body?: string,
    extraHeaders?: Record<string, string>
  ): Promise<T> {

    if (this.suppressLocalTracking()) return {} as T;

    let attempt = 0;

    while (attempt <= FlowGridTransport.MAX_RETRIES) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), FlowGridTransport.TIMEOUT_MS);

      try {
        const res = await fetch(url, {
          method,
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${this.apiKey}`,
            ...extraHeaders,
          },
          body,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        // Retry on 5xx (server transient errors)
        if (res.status >= 500 && attempt < FlowGridTransport.MAX_RETRIES) {
          attempt++;
          await this.backoff(attempt);
          continue;
        }

        // 204 — the site's own per-page tracking rule says stop. Core answers a
        // blocked page with a bare status line: no body, and no error status to
        // put a red line in the customer's devtools. Handled before the `res.ok`
        // branch because a 204 *is* ok, and `res.json()` on an empty body throws
        // straight into the retry path below.
        if (res.status === 204) {
          FlowGridTransport.notePathBlocked();
          return {} as T;
        }

        if (!res.ok) {
          const text = await this.safeRead(res);
          // Legacy 403 PATH_BLOCKED, from a core that predates the 204. Kept
          // because the CDN serves this SDK to sites whose core we do not
          // control the deploy order of — a new bundle must not start throwing
          // against an old server.
          if (res.status === 403 && text.includes('"PATH_BLOCKED"')) {
            FlowGridTransport.notePathBlocked();
            return {} as T;
          }
          // Any other rejection resolves silently too — see noteRequestFailure.
          FlowGridTransport.noteRequestFailure(`${res.status} ${res.statusText}`);
          return {} as T;
        }

        return await res.json();

      } catch (error) {
        clearTimeout(timeoutId);

        const isNetworkError =
          error instanceof Error &&
          (error.name === "AbortError" || error.message.includes("fetch"));

        if (isNetworkError && attempt < FlowGridTransport.MAX_RETRIES) {
          attempt++;
          await this.backoff(attempt);
          continue;
        }

        // Final failure — try sendBeacon, then buffer to localStorage
        if (attempt >= FlowGridTransport.MAX_RETRIES) {
          const beaconSent = body && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function'
            ? navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))
            : false;

          if (!beaconSent) {
            this.bufferEvent(url, method, body);
          }
        }

        FlowGridTransport.noteRequestFailure(
          error instanceof Error && error.name === "AbortError"
            ? "timed out"
            : error instanceof Error ? error.message : String(error)
        );
        return {} as T;
      }
    }

    // Unreachable: the loop body always returns on success or resolves
    // silently on permanent failure (the SDK never throws for transport
    // problems). The return satisfies TS on the bounded loop.
    FlowGridTransport.noteRequestFailure("exhausted retries");
    return {} as T;
  }

  /**
   * Exponential backoff with full jitter: ~1s, ~2s, ~4s (capped at 30s),
   * each randomised across its window.
   *
   * The jitter is the important part. Without it, every SDK instance that
   * failed during a backend blip retries at the *same* moment (t+1s, t+2s, …),
   * producing synchronized request waves — a self-inflicted thundering herd
   * that knocks the backend over again the instant it recovers. Spreading each
   * retry uniformly across its window de-synchronises the fleet so recovery
   * traffic ramps smoothly instead of spiking.
   */
  private backoff(attempt: number): Promise<void> {
    const ceiling = Math.min(Math.pow(2, attempt - 1) * 1000, 30_000);
    const ms = Math.random() * ceiling;
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Validates event name and payload.
   *
   * @param eventName - Event identifier.
   * @param properties - Associated event properties.
   *
   * @throws {Error} If validation fails.
   */
  private validateInput(eventName: string, properties: Record<string, any>) {

    if (!eventName || typeof eventName !== "string" || !eventName.trim()) {
      throw new Error("Event name must be a non-empty string");
    }

    if (eventName.length > 100) {
      throw new Error("Event name must be less than 100 characters");
    }

    let serialized: string;

    try {
      serialized = JSON.stringify({
        web_id: this.webId,
        visitor_id: this.visitorId ?? '',
        event_name: eventName,
        properties,
      });
    } catch {
      throw new Error("Properties must be JSON serializable");
    }

    if (new TextEncoder().encode(serialized).length > FlowGridTransport.MAX_PAYLOAD_SIZE) {
      throw new Error("Event payload too large (max 10KB)");
    }

    const sensitiveKeys = ["password", "token", "secret", "apikey", "authorization"];
    const lower = serialized.toLowerCase();

    for (const key of sensitiveKeys) {
      if (lower.includes(key)) {
        console.warn(
          `SDK Warning: Properties may contain sensitive data (${key}).`
        );
      }
    }
  }

  /**
   * Normalizes endpoint input.
   * Ensures protocol is present and removes trailing slashes.
   *
   * @param endpoint - Raw endpoint string.
   * @returns Normalized endpoint URL.
   */
  private normalizeEndpoint(endpoint: string): string {
    let clean = endpoint.trim();

    if (!clean.startsWith("http://") && !clean.startsWith("https://")) {
      clean = `https://${clean}`;
    }

    return clean.replace(/\/+$/, "");
  }

  /**
   * Safely reads response text without throwing.
   *
   * @param res - Fetch Response object.
   * @returns Response text or empty string.
   */
  private async safeRead(res: Response): Promise<string> {
    try {
      return await res.text();
    } catch {
      return "";
    }
  }
}
